import { useEffect, useRef } from 'react';

/**
 * Makes sure GET /api/v1/users has actually run for the signed-in user.
 *
 * `users.has_completed_tutorial` reaches redux through exactly one action —
 * FINISHED_GET_USER_INFO — and nothing app-wide dispatched it. `containers/App`
 * runs `validateAuth`, which authenticates but never fetches the profile;
 * `containers/Home` calls `getUserInfo()` only when it has no cached `m8_emr`,
 * and Home is mounted by the `/app` route alone. So an EMR user (cached emr) or
 * anyone landing straight on `/app/uploads` never hydrated the flag, and the
 * durable "don't offer the tutorials again" bit was unreadable for exactly the
 * returning users it exists for.
 *
 * LearnMedsender is the app-wide owner of that flag, so it is the right place to
 * guarantee the fetch rather than hoping a route did it.
 *
 * ONE attempt per user. A failure is not retried: `useTutorialProgress` and the
 * auto-open latch both refuse to act on a failed hydration (see
 * `reducers/userProfile` `userInfoLoadFailed`), and the safe direction for a
 * one-shot is "don't fire", not "fire on defaults".
 */
const useUserProfileHydration = ({
  userEmail,
  impersonating,
  userInfoLoaded,
  userInfoLoading,
  getUserInfo,
}) => {
  const requestedForRef = useRef(null);
  // Read at fire time, not at effect-schedule time — see the deferral below.
  const settledRef = useRef({ loaded: userInfoLoaded, loading: userInfoLoading });
  settledRef.current = { loaded: userInfoLoaded, loading: userInfoLoading };

  useEffect(() => {
    if (impersonating || !userEmail) return undefined;
    if (userInfoLoaded || userInfoLoading) return undefined;
    if (requestedForRef.current === userEmail) return undefined;

    /**
     * Deferred a tick so this cannot double-fetch alongside `containers/Home`.
     *
     * Home dispatches from `componentDidMount`, which React runs BEFORE any
     * passive effect in the same commit — but this effect's props were captured
     * during the render that preceded it, so they still say "not loading" and a
     * naive fetch here would duplicate Home's. A macrotask lets Home's
     * BEGIN_GET_USER_INFO land in the store and re-render this subtree; the ref
     * read below then sees the real state.
     */
    const timer = setTimeout(() => {
      const { loaded, loading } = settledRef.current;
      if (loaded || loading) return;
      requestedForRef.current = userEmail;
      // The action rejects on a failed request; FAILED_GET_USER_INFO has already
      // recorded that in the store, which is what consumers read.
      Promise.resolve(getUserInfo()).catch(() => {});
    }, 0);

    return () => clearTimeout(timer);
  }, [userEmail, impersonating, userInfoLoaded, userInfoLoading, getUserInfo]);
};

export default useUserProfileHydration;
